import { PublicClientApplication } from '@azure/msal-browser';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import modernAuthenticationService from '../../services/ModernAuthenticationService';

interface MsalContextType {
  instance: PublicClientApplication;
  isInitialized: boolean;
}

const MsalContext = createContext<MsalContextType | undefined>(undefined);

interface MsalProviderProps {
  children: ReactNode;
}

/**
 * Custom MSAL Provider component that provides the MSAL instance to child components
 * Tracks MSAL initialization status
 * Provides the MSAL instance and its initialization state to child components via React context
 * Exports a useMsal hook for components to access the MSAL context
 */
export const MsalProvider: React.FC<MsalProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const msalInstance = modernAuthenticationService.getMsalInstance();

  useEffect(() => {
    let isMounted = true;

    const initializeMsal = async () => {
      try {
        await modernAuthenticationService.waitForInitialization();
        if (isMounted) {
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('MSAL initialization failed:', error);
      }
    };

    if (!modernAuthenticationService.initialized) {
      initializeMsal();
    } else {
      setIsInitialized(true);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const contextValue: MsalContextType = {
    instance: msalInstance,
    isInitialized
  };

  return (
    <MsalContext.Provider value={contextValue}>
      {children}
    </MsalContext.Provider>
  );
};

/**
 * Hook to access the MSAL context
 */
export const useMsal = (): MsalContextType => {
  const context = useContext(MsalContext);
  if (!context) {
    throw new Error('useMsal must be used within a MsalProvider');
  }
  return context;
};
